import React, { useState } from 'react';
import { Alert, Form, Button, Modal } from 'react-bootstrap';

import { useTranslation } from '../../../common/hooks/useTranslation';
import DynamicTextarea from '../../../app/components/DynamicTextarea';
import mathUtils from '../../../utils/mathUtils';

interface PasteModalProps {
  show: boolean;
  onHide: () => void;
  onInsert: (values: string[]) => void;
  title: string;
  maxRows?: number;
  expectedType?: 'text' | 'number';
}

export const PasteModal: React.FC<PasteModalProps> = ({
  show,
  onHide,
  onInsert,
  title,
  maxRows,
  expectedType,
}) => {
  const [pasteText, setPasteText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { t, tk } = useTranslation('fvsLng');

  const validateValues = (
    values: string[],
  ): { valid: boolean; error?: string } => {
    if (expectedType === 'number') {
      const invalidValues = values.filter(val => !mathUtils.isValidNumber(val));
      if (invalidValues.length > 0) {
        const displayValues = invalidValues.slice(0, 3).join(', ');
        const suffix =
          invalidValues.length > 3
            ? ` ${t(tk.finance.invalidNumberFormatMore, {
                count: invalidValues.length - 3,
              })}`
            : '';
        return {
          valid: false,
          error: t(tk.finance.invalidNumberFormat, {
            values: displayValues,
            suffix,
          }),
        };
      }
    }
    return { valid: true };
  };

  const handleInsert = () => {
    const values = pasteText
      .split('\n')
      .map(val => val.trim())
      .filter(val => val.length > 0);

    if (values.length === 0) {
      return;
    }

    if (maxRows && values.length > maxRows) {
      setError(
        t(tk.finance.tooManyValues, {
          maxRows,
          count: values.length,
        }),
      );
      return;
    }

    const validation = validateValues(values);
    if (!validation.valid) {
      setError(validation.error || '');
      return;
    }

    const normalizedValues =
      expectedType === 'number'
        ? values.map(mathUtils.normalizeNumberValue)
        : values;

    onInsert(normalizedValues);
    setPasteText('');
    setError(null);
    onHide();
  };

  const handleHide = () => {
    setPasteText('');
    setError(null);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      size="lg"
      backdrop="static"
      backdropClassName="modal-on-modal-backdrop"
      className="modal-on-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        <Form.Group className="mb-3">
          <Form.Label>{t(tk.finance.pasteDataLabel)}</Form.Label>
          <DynamicTextarea
            minRows={8}
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.stopPropagation();
              }
            }}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide}>
          {t(tk.button.cancel)}
        </Button>
        <Button variant="primary" onClick={handleInsert}>
          {t(tk.button.insert)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
